import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import CardapioCache from 'cardapiocache';
import { RestaurantService } from '../restaurant.service';

/** Estado exposto em `window.__mpSsr` para a bancada ler (o yellow não emite telemetria de funil). */
export interface EstadoDoSsr {
    render: 'ssr';
    hashDoHtml: string;
    hashAtual: string | null;
    recuou: boolean;
}

/** Guarda anti-loop: no máximo um recuo por sessão de aba. */
export const CHAVE_RECUO_NA_SESSAO = 'mp_ssr_recuou';
/** Cookie que a CloudFront Function lê para servir o render de hoje (CSR) em vez do HTML pronto. */
export const COOKIE_CSR = 'mp_csr';

/**
 * Confere, depois da hidratação, se o cardápio que veio no HTML pré-renderizado ainda é o
 * atual — e, se não for, recua para o render de hoje (CSR) com dado novo.
 *
 * O HTML é gerado uma vez e servido a todos até ser gerado de novo; nesse meio-tempo a loja
 * pode ter mudado preço, estoque ou produto. A pergunta é a mesma que o `CardapioCache` faz a
 * cada visita (qual o hash atual no AppSync?), comparada com o hash que o servidor usou. Se
 * diferem, o cliente grava o cookie `mp_csr` e recarrega: a borda passa a entregar o
 * `index.html` e o boot segue o caminho de sempre, que baixa o cardápio novo.
 *
 * Só age quando o cardápio veio mesmo do servidor (`restaurantService.htmlDoServidor`); em
 * todo boot sem SSR é um no-op. Falha de rede na consulta mantém o HTML (melhor mostrar o que
 * veio do que recarregar às cegas).
 */
@Injectable({ providedIn: 'root' })
export class FrescorDoHtmlService {
    /** Injetável nos specs: a consulta real é a mesma do `CardapioCache`. */
    consultarHashAtual: (url: string) => Promise<string | null> =
        (url) => new CardapioCache(url).fetch_cardapio_hash();

    constructor(
        private restaurantService: RestaurantService,
        @Inject(PLATFORM_ID) private platformId: Object,
        @Inject(DOCUMENT) private doc: Document,
    ) {}

    async conferir(): Promise<EstadoDoSsr | null> {
        if (!isPlatformBrowser(this.platformId)) return null;
        const html = this.restaurantService.htmlDoServidor;
        if (!html) return null;

        const janela: any = this.doc.defaultView || {};
        const estado: EstadoDoSsr = { render: 'ssr', hashDoHtml: html.hash, hashAtual: null, recuou: false };
        janela.__mpSsr = estado;

        let hashAtual: string | null = null;
        try {
            hashAtual = await this.consultarHashAtual(html.url);
        } catch (e) {
            return estado;
        }
        estado.hashAtual = hashAtual;
        if (!hashAtual || hashAtual === html.hash) return estado;

        this.recuarParaCsr(estado, janela);
        return estado;
    }

    private recuarParaCsr(estado: EstadoDoSsr, janela: any): void {
        // Sem sessionStorage não há como garantir um recuo só — e recarregar em loop é pior
        // que mostrar um cardápio de minutos atrás. Então, sem guarda, não recua.
        try {
            if (janela.sessionStorage.getItem(CHAVE_RECUO_NA_SESSAO)) return;
            janela.sessionStorage.setItem(CHAVE_RECUO_NA_SESSAO, '1');
        } catch (e) {
            return;
        }
        estado.recuou = true;
        this.doc.cookie = `${COOKIE_CSR}=1; max-age=120; path=/; SameSite=Lax`;
        janela.location.reload();
    }
}
